import { useRef, useState, useEffect } from "react";
import { SimpleSelect } from "V2.0/components/SimpleSelect";
import { useDebouncedSearch } from "V2.0/HomePages/Threads/models/useDebouncedSearch";
import { SearchBar } from "./Search";
import { Button } from "V2.0/common/button";
import { searchVoices } from "./service";
import { message } from "antd";
import { VoicesSearchResultsTable } from "./VoicesSearchResultsTable";
import { VoiceAddForm } from "./VoiceAddForm";
import { languagesList } from "./languages";
import { showErrorToast } from "V2.0/utils";

const genderList = [
  { id: 0, label: "Any", value: "" },
  { id: 1, label: "Male", value: "male" },
  { id: 2, label: "Female", value: "female" },
];

function VoicesSearch(props) {
  const { fetchVoices } = props;
  const [voiceSearchResults, setVoiceSearchResults] = useState({
    data: [],
    hasMore: false,
    isLoading: true,
    pageSize: 20,
    page: 1,
  });
  const { searchTerm, updateSearchTerm } = useDebouncedSearch();
  const searchRef = useRef(null);
  const [gender, setGender] = useState(genderList[0]);
  const [language, setLanguage] = useState(languagesList[0]);
  const [showAddVoiceForm, setShowAddVoiceForm] = useState({
    shouldShow: false,
    voice: null,
  });

  function handleSearch(e) {
    updateSearchTerm(e.target.value);
  }

  async function handleFetchVoices(payload) {
    try {
      const response = await searchVoices({ payload });
      if (response.status === 200) {
        return response;
      }
    } catch (e) {
      showErrorToast(e, "Search voices failed");
      setVoiceSearchResults((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
      throw e;
    }
  }

  async function handleLoadMore() {
    const payload = {
      ttsServiceProvider: "elevenlabs",
      search: searchTerm,
      gender: gender.value,
      language: language.value,
      pageSize: voiceSearchResults.pageSize,
      page: voiceSearchResults.page + 1,
    };

    setVoiceSearchResults((prevState) => {
      return {
        ...prevState,
        isLoading: true,
      };
    });

    try {
      const response = await handleFetchVoices(payload);
      setVoiceSearchResults((prevState) => {
        return {
          ...prevState,
          data: [...prevState.data, ...response.data.voices],
          hasMore: response.data.has_more,
          isLoading: false,
          page: payload.page,
        };
      });
    } catch (e) {
      showErrorToast(e, "Failed to load more voices");
    }
  }

  async function handleSearchClick(page) {
    const payload = {
      ttsServiceProvider: "elevenlabs",
      search: searchTerm,
      gender: gender.value,
      language: language.value,
      pageSize: voiceSearchResults.pageSize,
      page,
    };

    setVoiceSearchResults((prevState) => {
      return {
        ...prevState,
        data: [],
        hasMore: false,
        isLoading: true,
      };
    });

    try {
      const response = await handleFetchVoices(payload);

      setVoiceSearchResults((prevState) => ({
        ...prevState,
        data: response.data.voices,
        hasMore: response.data.has_more,
        isLoading: false,
        page,
      }));
    } catch (e) {
      showErrorToast(e, "Failed to fetch voices");
    }
  }

  useEffect(() => {
    handleSearchClick(1);
  }, []);

  return (
    <>
      <div className="min-h-[calc(100vh-300px)]">
        <div>
          <div className="flex items-center gap-x-2">
            <div className="w-[400px]">
              <SearchBar
                placeholder="Search by VoiceID or Name"
                value={searchTerm}
                handleSearch={handleSearch}
              />
            </div>
            <div className="w-[200px]">
              <SimpleSelect
                selectList={genderList}
                selected={gender}
                onSelect={setGender}
                title="Gender"
              />
            </div>
            <div className="w-[200px]">
              <SimpleSelect
                selectList={languagesList}
                selected={language}
                onSelect={setLanguage}
                title="Language"
              />
            </div>
            <div>
              <Button onClick={() => handleSearchClick(1)}>Search</Button>
            </div>
          </div>
          <div className="border-t rounded-lg mt-8 h-full max-h-[calc(100vh-350px)] overflow-auto">
            <VoicesSearchResultsTable
              voices={voiceSearchResults}
              onAdd={(item) =>
                setShowAddVoiceForm({
                  shouldShow: true,
                  voice: item,
                })
              }
              onLoadMore={handleLoadMore}
            />
          </div>
        </div>
      </div>
      {showAddVoiceForm.shouldShow && (
        <VoiceAddForm
          voice={showAddVoiceForm.voice}
          onCancel={() =>
            setShowAddVoiceForm({
              shouldShow: false,
              voice: null,
            })
          }
          fetchVoices={fetchVoices}
        />
      )}
    </>
  );
}

export { VoicesSearch };
